import { useInViewport } from '@mantine/hooks';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { MutableRefObject } from 'react';

import WidgetError from 'components/Dashboard/components/WidgetError';
import WidgetLoadingIndicator from 'components/Dashboard/components/WidgetLoadingIndicator';
import { useDashboardContext } from '../../../../screens/dashboard/Dashboard';
import { TWidget } from '../../../../utility/types/dataProvider';
import WidgetHeading from '../../components/WidgetHeading';
import { TRss } from './constants';
import RssImageCards from './RssImageCards';
import RssListCards from './RssListCards';
import { ErrorBoundary } from 'react-error-boundary';

export type UseInViewportRefType = ReturnType<typeof useInViewport>['ref'];
export type UseRefType = MutableRefObject<HTMLDivElement>;
export type PropsRefType = UseInViewportRefType | UseRefType | any; // fix type

type RssLayoutProps = {
  widget: TWidget;
  data: any;
  isLoading: boolean;
  isError: boolean;
  viewportRef: PropsRefType;
};

const RssLayout = ({ widget, data, isLoading, isError, viewportRef }: RssLayoutProps) => {
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { columns } = useDashboardContext();

  const feed: TRss[] = data?.results || [];
  const feedLength = isFullScreen ? 1 : columns?.includes('100') ? 5 : 3;

  // List-card view.
  if (widget?.display?.view === 'listcard') {
    return (
      <RssListCards
        viewportRef={viewportRef}
        rssFeed={feed}
        isError={isError}
        isLoading={isLoading}
        id={widget?.id}
      />
    );
  }

  // Image card view.
  return (
    <Box
      id={widget?.id}
      ref={viewportRef}
      sx={(theme) => ({
        backgroundColor:
          theme.palette.mode === 'light'
            ? theme.blox.colors.white
            : theme.blox.colors.offblack,
        p: 2,
        borderRadius: 1,
        boxShadow: 1,
        mb: { xs: 1, md: 0 },
      })}
    >
      <WidgetHeading>{widget?.title}</WidgetHeading>

      {/* RSS' Content. */}
      <ErrorBoundary fallback={<WidgetError />}>
        {isLoading ? (
          <WidgetLoadingIndicator />
        ) : isError ? (
          <WidgetError />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${feedLength}, 1fr)`,
              gap: 1,
            }}
          >
            {feed.slice(0, feedLength).map((rss, i) => (
              <RssImageCards
                key={rss?.guid || i}
                {...rss}
              />
            ))}
          </Box>
        )}
      </ErrorBoundary>
    </Box>
  );
};

export default RssLayout;
